import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  CardContent,
  IconButton,
  TextField,
  Tooltip,
  Typography,
  Zoom,
  useMediaQuery,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { ArrowBack as ArrowBackIcon, Edit, Add, Delete } from "@mui/icons-material";
import {
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineItem,
  TimelineSeparator,
} from "@mui/lab";
import dayjs from "dayjs";

import { setAllEmployeeAttendenceData } from "../../../Slices/UserDataSlice.js";
import { setToast, setToastMsg, setToastType } from "../../../Slices/toastSlice.js";
import { formatDateSlashTo_, renderAsterisk } from "../../ReusableComponents/UtilityFunctions.js";
import { editTimer } from "../../../Services/AttendanceManageServices.js";
import { errorMessage } from "../../../Services/axiosInstance.js";
import { CustomDateTimePicker, SaveButton, DateTypography, EditTimeMainBox, EditTimeMainCard, EditTimeTitleBox, Timelines } from "../../../Styles/UpdateEmployee/UpdateAttendanceStyle.js";
import { CancelButton } from "../../../Styles/UpdateEmployee/UpdateContactStyle.js";

function EditTimer({ change, record, empId, presentDate }) {
  const dispatch = useDispatch();
  const isMobile = useMediaQuery("(max-width:600px)");
  const isDarkTheme = useSelector((state) => state.theme.isDarkTheme);

  const [id, setId] = useState("");
  const [errorReason, setErrorReason] = useState("");
  const [errorStart, setErrorStart] = useState("");
  const [errorEnd, setErrorEnd] = useState("");
  const [displayError, setDisplayError] = useState("");
  const [displaySuccess, setDisplaySuccess] = useState("");
  const [selectedRecordReason, setSelectedRecordReason] = useState("");
  const [selectedDate, setSelectedDate] = useState(0);
  const [disableSave, setDisableSave] = useState(false);
  const [startTimeSelected, setStartTimeSelected] = useState(
    dayjs("2022-04-17T15:30")
  );
  const [endTimeSelected, setEndTimeSelected] = useState(
    dayjs("2022-04-17T15:30")
  );
  const [realTimeError, setRealTimeError] = useState("");
  const [edit, setEdit] = useState(false);
  const [addNew, setAddNew] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, index: null });
  const [editingIndex, setEditingIndex] = useState(null);
  const [pendingChanges, setPendingChanges] = useState([]);
  const records = record;

  const handleInputChange = (event) => {
    setErrorReason("");
    setSelectedRecordReason(event.target.value);
  };

  // Real-time validation function
  const validateTimeSelection = (startTime, endTime, targetDate) => {
    const errors = [];
    
    // Check if times are on the same day as target date
    const isStartSameDay = dayjs(startTime).isSame(targetDate, "day");
    const isEndSameDay = dayjs(endTime).isSame(targetDate, "day");
    
    if (!isStartSameDay) {
      errors.push("Start time must be on the selected date");
    }
    if (!isEndSameDay) {
      errors.push("End time must be on the selected date");
    }
    
    // Check if end time is after start time
    if (isStartSameDay && isEndSameDay && dayjs(endTime).isBefore(dayjs(startTime))) {
      errors.push("End time must be after start time");
    }
    
    // Check for reasonable time ranges (not more than 24 hours)
    if (isStartSameDay && isEndSameDay) {
      const durationHours = dayjs(endTime).diff(dayjs(startTime), 'hour', true);
      if (durationHours > 24) {
        errors.push("Duration cannot exceed 24 hours");
      }
      if (durationHours < 0) {
        errors.push("Invalid time range");
      }
    }
    
    return errors;
  };

  // Handle start time change with real-time validation
  const handleStartTimeChange = (newValue) => {
    setStartTimeSelected(newValue);
    setErrorStart("");
    setRealTimeError("");
    
    if (edit && endTimeSelected) {
      const errors = validateTimeSelection(newValue, endTimeSelected, selectedDate);
      if (errors.length > 0) {
        setRealTimeError(errors[0]);
        setErrorStart(errors[0]);
      }
    }
  };

  // Handle end time change with real-time validation
  const handleEndTimeChange = (newValue) => {
    setEndTimeSelected(newValue);
    setErrorEnd("");
    setRealTimeError("");
    
    if (edit && startTimeSelected) {
      const errors = validateTimeSelection(startTimeSelected, newValue, selectedDate);
      if (errors.length > 0) {
        setRealTimeError(errors[0]);
        setErrorEnd(errors[0]);
      }
    }
  };

  // Validate against existing records when adding new
  const validateAgainstExistingRecords = (newStartTime, newEndTime) => {
    if (!records || records.length === 0) return [];
    
    const conflicts = [];
    const newStart = dayjs(newStartTime);
    const newEnd = dayjs(newEndTime);
    
    records.forEach((record, index) => {
      const existingStart = dayjs(record.startTime);
      const existingEnd = dayjs(record.endTime);
      
      // Skip if this is the record being edited
      if (editingIndex === index) return;
      
      // Check for overlapping time ranges
      if (
        (newStart.isBefore(existingEnd) && newEnd.isAfter(existingStart)) ||
        (newStart.isSame(existingStart) || newEnd.isSame(existingEnd))
      ) {
        conflicts.push(`Time conflicts with existing record (${extractTime(record.startTime)} - ${extractTime(record.endTime)})`);
      }
    });
    
    return conflicts;
  };

  function extractTime(timestamp) {
    if (timestamp === 0) {
      return "00:00";
    }
    const dateTime = new Date(timestamp);
    const time = dateTime.toLocaleTimeString([], {
      hour12: true,
      hour: "2-digit",
      minute: "2-digit",
    });
    return time;
  }

  function formatDateTime(timestamp) {
    if (timestamp === 0) {
      const dateTime = new Date();
      const year = dateTime.getFullYear();
      const month = String(dateTime.getMonth() + 1).padStart(2, "0");
      const day = String(dateTime.getDate()).padStart(2, "0");
      const hours = String(dateTime.getHours()).padStart(2, "0");
      const minutes = String(dateTime.getMinutes()).padStart(2, "0");
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    } else {
      const dateTime = new Date(timestamp);
      const year = dateTime.getFullYear();
      const month = String(dateTime.getMonth() + 1).padStart(2, "0");
      const day = String(dateTime.getDate()).padStart(2, "0");
      const hours = String(dateTime.getHours()).padStart(2, "0");
      const minutes = String(dateTime.getMinutes()).padStart(2, "0");
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    }
  }

  const updatedRecord = (index) => {
    setDisplayError("");
    setDisplaySuccess("");
    setEdit(true);
    setEditingIndex(index);
    setId(records[index]._id);
    setSelectedDate(dayjs(formatDateTime(records[index].startTime)));
    setSelectedRecordReason(records[index].reason);
    setStartTimeSelected(dayjs(formatDateTime(records[index].startTime)));
    setEndTimeSelected(dayjs(formatDateTime(records[index].endTime)));
  };

  const saveTime = async () => {
    // Clear previous errors
    setErrorStart("");
    setErrorEnd("");
    setErrorReason("");
    setRealTimeError("");
    
    // Validate date matching
    const isSamestart =
      dayjs(startTimeSelected).isSame(selectedDate, "day") &&
      dayjs(startTimeSelected).isSame(selectedDate, "month") &&
      dayjs(startTimeSelected).isSame(selectedDate, "year");
    const isSameEnd =
      dayjs(endTimeSelected).isSame(selectedDate, "day") &&
      dayjs(endTimeSelected).isSame(selectedDate, "month") &&
      dayjs(endTimeSelected).isSame(selectedDate, "year");
    
    // Basic validations
    if (!isSamestart) {
      setErrorStart("Start time must be on the selected date");
      return;
    }
    if (!isSameEnd) {
      setErrorEnd("End time must be on the selected date");
      return;
    }
    if (!selectedRecordReason) {
      setErrorReason("Reason Required.");
      return;
    }
    
    const startTimestamp = startTimeSelected.valueOf();
    const endTimestamp = endTimeSelected.valueOf();
    
    if (startTimestamp > endTimestamp) {
      setErrorStart("Start time must be before end time");
      return;
    }
    
    // Check for conflicts with existing records
    const conflicts = validateAgainstExistingRecords(startTimeSelected, endTimeSelected);
    if (conflicts.length > 0) {
      setRealTimeError(conflicts[0]);
      setErrorStart(conflicts[0]);
      return;
    }
    
    // All validations passed
    const totalTime = endTimestamp - startTimestamp;
    const changeData = {
      id: addNew ? null : id,
      totalTime,
      endTimestamp,
      startTimestamp,
      selectedRecordReason,
      empId,
      operation: addNew ? 'add' : 'update'
    };
    
    try {
      setDisableSave(true);
      const postData = JSON.stringify(changeData);
      const response = await editTimer(postData);
      
      if (response.status === 201 || response.status === 200) {
        setDisplaySuccess(response.data.message);
        setEdit(false);
        setAddNew(false);
        setEditingIndex(null);
        dispatch(setAllEmployeeAttendenceData(response.data.attendence));
        setDisableSave(false);
        dispatch(setToast(true));
        dispatch(setToastMsg(response.data.message));
        dispatch(setToastType("success"));
      }
    } catch (error) {
      setDisplayError(errorMessage ?? "");
      setDisableSave(false);
    }
  };
  
  const deleteRecord = async (index) => {
    try {
      setDisableSave(true);
      const postData = JSON.stringify({
        id: records[index]._id,
        empId,
        operation: 'delete',
        startTimestamp: records[index].startTime,
        endTimestamp: records[index].endTime,
        totalTime: records[index].totalTime,
        selectedRecordReason: records[index].reason
      });
      const response = await editTimer(postData);
      
      if (response.status === 201 || response.status === 200) {
        setDisplaySuccess(response.data.message);
        setDeleteConfirm({ open: false, index: null });
        dispatch(setAllEmployeeAttendenceData(response.data.attendence));
        setDisableSave(false);
        dispatch(setToast(true));
        dispatch(setToastMsg(response.data.message));
        dispatch(setToastType("success"));
      }
    } catch (error) {
      setDisplayError(errorMessage ?? "");
      setDisableSave(false);
    }
  };
  
  const addNewRecord = () => {
    setDisplayError("");
    setDisplaySuccess("");
    setRealTimeError("");
    setErrorStart("");
    setErrorEnd("");
    setAddNew(true);
    setEdit(true);
    setEditingIndex(null);
    setId("");
    setSelectedDate(dayjs(presentDate, "DD-MM-YYYY"));
    setSelectedRecordReason("");
    setStartTimeSelected(dayjs(presentDate, "DD-MM-YYYY").hour(9).minute(0)); // Default to 9:00 AM
    setEndTimeSelected(dayjs(presentDate, "DD-MM-YYYY").hour(17).minute(0)); // Default to 5:00 PM
  };
  
  const cancelEdit = () => {
    setEdit(false);
    setAddNew(false);
    setEditingIndex(null);
    setDisableSave(false);
    setErrorReason("");
    setErrorStart("");
    setErrorEnd("");
    setRealTimeError("");
  };
  return (
    <EditTimeMainBox>
      <EditTimeTitleBox>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <IconButton onClick={change}>
            <ArrowBackIcon color="primary" />
          </IconButton>

          <Typography variant="h6" sx={{ fontWeight: "bold" }}>
            Update Timer
          </Typography>
        </Box>
      </EditTimeTitleBox>
      <EditTimeMainCard>
        <CardContent sx={{ padding: isMobile ? "16px 0px" : "16px" }}>
          <Box sx={{ margin: "0px 10px 0px 10px" }}>
            <Box sx={{ color: "var(--secondary-text-color)" }}>
              <DateTypography
                variant="h6">
                Date:- {formatDateSlashTo_(presentDate)}
              </DateTypography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'var(--secondary-text-color)' }}>
                Timer Records
              </Typography>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={addNewRecord}
                disabled={edit}
                sx={{
                  backgroundColor: 'var(--primary-color)',
                  '&:hover': { backgroundColor: 'var(--primary-hover-color)' }
                }}
              >
                Add Record
              </Button>
            </Box>
            <Box>
              {records?.length === 0 ?
                <Typography sx={{ display: 'flex', justifyContent: 'center', color: 'var(--secondary-text-color)' }}>Not Punched In</Typography>
                : null}
              {records.map((record, index) => (
                <Timelines sx={{ margin: isMobile ? "7px 0px 16px 0px" : "16px 0px 16px 0px" }} align="left" key={index}>
                  <TimelineItem key={index}>
                    <TimelineSeparator>
                      <TimelineDot />
                      <TimelineConnector />
                    </TimelineSeparator>
                    <TimelineContent key={index}>
                      <Box key={index}>
                        <TextField
                          disabled
                          label="Start Time"
                          value={extractTime(record.startTime)}
                          style={{ padding: isMobile ? "0px 0px 15px 0px" : "0px 10px 10px 0px" }}
                        />
                        <TextField
                          disabled
                          label="End Time"
                          value={extractTime(record.endTime)}
                          style={{ padding: isMobile ? "0px 0px 15px 0px" : "0px 10px 10px 0px" }}
                        />
                        <TextField
                          label="Reason"
                          disabled
                          value={record.reason}
                        />
                        <Tooltip
                          title="Edit Data"
                          arrow
                          disableInteractive
                          TransitionComponent={Zoom}
                        >
                          <IconButton
                            onClick={() => updatedRecord(index)}
                            sx={{ color: "var(--secondary-text-color)" }}
                            disabled={edit}
                          >
                            <Edit />
                          </IconButton>
                        </Tooltip>
                        <Tooltip
                          title="Delete Record"
                          arrow
                          disableInteractive
                          TransitionComponent={Zoom}
                        >
                          <IconButton
                            onClick={() => setDeleteConfirm({ open: true, index })}
                            sx={{ color: "red" }}
                            disabled={edit}
                          >
                            <Delete />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TimelineContent>
                  </TimelineItem>
                </Timelines>
              ))}
            </Box>
            <hr />
            {edit ? (
              <>
                <Box sx={{ margin: "20px 0px 0px 0px" }}>
                  <Box sx={{ flexDirection: isMobile ? "column" : "row" }} display="flex">
                    <Box sx={{ padding: isMobile ? "10px 0px 10px 20px" : "10px 10px 10px 10px" }}>
                      <CustomDateTimePicker
                        value={startTimeSelected}
                        onChange={handleStartTimeChange}
                        label={
                          <>
                            Start Time
                            {renderAsterisk()}
                          </>
                        }
                      />
                      <Typography sx={{ color: "red" }}>
                        {errorStart}
                      </Typography>
                    </Box>
                    <Box
                      sx={{ padding: isMobile ? "10px 0px 10px 20px" : "10px 10px 10px 10px" }}
                    >
                      <CustomDateTimePicker
                        value={endTimeSelected}
                        onChange={handleEndTimeChange}
                        label={
                          <>
                            End Time
                            {renderAsterisk()}
                          </>
                        }
                      />
                      <Typography sx={{ color: "red" }}>{errorEnd}</Typography>
                    </Box>
                    <Box
                      sx={{ padding: isMobile ? "10px 0px 10px 20px" : "10px 10px 10px 10px" }}
                    >
                      <TextField
                        value={selectedRecordReason}
                        onChange={handleInputChange}
                        label={
                          <>
                            Reason
                            {renderAsterisk()}
                          </>
                        }
                        InputLabelProps={{
                          shrink: true,
                        }}
                        sx={{
                          "& fieldset legend > span": {
                            paddingLeft: "2px",
                            paddingRight: "0px",
                          },
                        }}
                      />
                      <Typography sx={{ color: "red" }}>
                        {errorReason}
                      </Typography>
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      padding: "10px 0px 0px 0px",
                      display: "flex",
                      justifyContent: isMobile && "flex-end"
                    }}
                  >
                    <SaveButton
                      sx={{ padding: isMobile ? "3px 8px" : "6px 16px", marginRight: "5px" }}
                      variant="contained"
                      onClick={saveTime}
                      disabled={disableSave || !!realTimeError}
                    >
                      Save
                    </SaveButton>

                    {/* <Box sx={{ display: "flex", alignItems: "center" }}>
                      {saveMessage && (
                        <Typography
                          sx={{
                            mr: 2,
                            color: isDarkTheme ? "#e0e0e0" : "#5e6e82",
                          }}
                        >
                          {saveMessage}
                        </Typography>
                      )}
                      {displayError && (
                        <Typography sx={{ mr: 2, color: "red" }}>
                          {displayError}
                        </Typography>
                      )}
                    </Box> */}
                    <CancelButton
                      disabled={disableSave}
                      isdarktheme={isDarkTheme ? "true" : "false"}
                      sx={{ padding: isMobile ? "3px 8px" : "6px 16px" }}
                      onClick={cancelEdit}
                    >
                      Cancel
                    </CancelButton>
                  </Box>
                </Box>
              </>
            ) : null}
            <Typography sx={{ color: "red" }}>{displayError}</Typography>
            <Typography sx={{ color: "green" }}>{displaySuccess}</Typography>
          </Box>
        </CardContent>
      </EditTimeMainCard>
      
      <Dialog
        open={deleteConfirm.open}
        onClose={() => setDeleteConfirm({ open: false, index: null })}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this timer record?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm({ open: false, index: null })}>Cancel</Button>
          <Button onClick={() => deleteRecord(deleteConfirm.index)} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </EditTimeMainBox>
  );
}

export default EditTimer;
